import {
  BeaconConnection,
  BeaconConnectionFormData,
  BeaconType
} from 'core/advertiser/BeaconConnection';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import { useLineSimpleBeaconFields } from 'containers/BeaconConnection/hooks/useLineSimpleBeaconFields';
import { FormConfig } from 'components/common/form/FormConfig';
import { useFormikContext } from 'formik';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { camelCase, omit } from 'lodash';
import { LineBeaconServiceType } from 'core/advertiser/LineBeacon';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import { useTenMaxBeaconFields } from './hooks/useTenMaxBeaconFields';

const advertiserManager: AdvertiserManager = new DefaultAdvertiserManager();

export type BeaconConnectionFormModel = {
  title: string;
  loading: boolean;
  initBeaconConnection: BeaconConnectionFormData;
  onSave: (beaconConnection: BeaconConnectionFormData) => void;
};

const beaconTypeOptions = createSelectOptionsFromEnum(
  BeaconType,
  'beaconType.',
  undefined,
  camelCase
);

export const useBeaconConnectionFormConfig = () => {
  const { values, setFieldValue } =
    useFormikContext<BeaconConnectionFormData>();
  const lineSimpleBeaconFields = useLineSimpleBeaconFields(values.beaconType);
  const tenMaxBeaconFields = useTenMaxBeaconFields(values.beaconType);

  const resetFields = useCallback(
    (beaconType: BeaconType) => {
      if (beaconType === BeaconType.TENMAX_BEACON) {
        setFieldValue('webhookType', undefined);
        setFieldValue('hardwareId', undefined);
        setFieldValue('channelSecret', undefined);
        setFieldValue('channelAccessToken', undefined);
        return;
      }
    },
    [setFieldValue]
  );

  const fields = useMemo(() => {
    const builder = new FormConfig.FieldsBuilder().addFormikSelect({
      label: i18n.t('beaconConnectionList.labels.beaconType'),
      name: 'beaconType',
      simpleValue: true,
      options: beaconTypeOptions,
      onChange: resetFields
    });

    const basicFields = builder.build();
    return [...basicFields, ...lineSimpleBeaconFields, ...tenMaxBeaconFields];
  }, [resetFields, lineSimpleBeaconFields, tenMaxBeaconFields]);

  const formConfig = useMemo(() => {
    return new FormConfig.Builder()
      .addSection(
        new FormConfig.SectionBuilder(fields).withShadow(false).build()
      )
      .build();
  }, [fields]);

  return formConfig;
};

export const useCreateBeaconConnectionFormModel = (
  advertiserId: number,
  onSaved: () => void
): BeaconConnectionFormModel => {
  const { loading, callAPIs } = useCallAPI();

  const onSave = useCallback(
    (beaconConnection: BeaconConnectionFormData) => {
      callAPIs(
        [
          async () => {
            let name = beaconConnection.name;
            const isLineBeaconAndTenMaxSupport =
              beaconConnection.beaconType === BeaconType.LINE_SIMPLE_BEACON &&
              'webhookType' in beaconConnection &&
              beaconConnection.webhookType === LineBeaconServiceType.TENMAX;
            if (isLineBeaconAndTenMaxSupport) {
              const lineBotInfo = await advertiserManager.getLineBotInfo(
                advertiserId,
                beaconConnection.channelAccessToken!
              );
              name = lineBotInfo.displayName;
            }
            return advertiserManager.createBeaconConnection(advertiserId, {
              ...beaconConnection,
              name
            });
          }
        ],
        () => {
          onSaved();
        }
      );
    },
    [advertiserId, callAPIs, onSaved]
  );

  const initBeaconConnection = useMemo(() => {
    return {
      beaconType: BeaconType.LINE_SIMPLE_BEACON,
      name: '',
      hardwareId: '',
      webhookType: '' as const,
      channelAccessToken: '',
      channelSecret: ''
    };
  }, []);

  return {
    title: i18n.t('beaconConnectionForm.titles.create'),
    loading,
    initBeaconConnection,
    onSave
  };
};

export const useEditBeaconConnectionFormModel = (
  advertiserId: number,
  beaconConnection: BeaconConnection,
  onSaved: () => void
): BeaconConnectionFormModel => {
  const { loading, callAPIs } = useCallAPI();

  const onSave = useCallback(
    (editedBeaconConnection: BeaconConnectionFormData) => {
      callAPIs(
        [
          async () => {
            let name = editedBeaconConnection.name;
            const isLineBeaconAndTenMaxSupport =
              editedBeaconConnection.beaconType ===
                BeaconType.LINE_SIMPLE_BEACON &&
              'webhookType' in editedBeaconConnection &&
              editedBeaconConnection.webhookType ===
                LineBeaconServiceType.TENMAX;
            if (isLineBeaconAndTenMaxSupport) {
              const lineBotInfo = await advertiserManager.getLineBotInfo(
                advertiserId,
                editedBeaconConnection.channelAccessToken!
              );
              name = lineBotInfo.displayName;
            }
            return advertiserManager.updateBeaconConnection(
              advertiserId,
              beaconConnection.id,
              { ...editedBeaconConnection, name }
            );
          }
        ],
        () => {
          onSaved();
        }
      );
    },
    [advertiserId, beaconConnection, callAPIs, onSaved]
  );

  return {
    title: i18n.t('beaconConnectionForm.titles.edit'),
    loading,
    initBeaconConnection: omit(
      beaconConnection,
      'id'
    ) as BeaconConnectionFormData,
    onSave
  };
};
